/**
 * Image Hotspots Block - Editor Component
 *
 * @package BrandyBlocks
 */

import { __ } from '@wordpress/i18n';
import { decodeEntities } from '@wordpress/html-entities';
import { useState, useRef, useCallback } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import {
	useBlockProps,
	InspectorControls,
	MediaPlaceholder,
	MediaReplaceFlow,
	MediaUploadCheck,
	BlockControls,
	PanelColorSettings,
	useSettings,
	store as blockEditorStore,
} from '@wordpress/block-editor';
import {
	PanelBody,
	Button,
	TextControl,
	TextareaControl,
	SelectControl,
	ComboboxControl,
} from '@wordpress/components';
import IconsModal from '../../components/IconsModal';
import {
	fromColorPreset,
	resolvePresetValue,
	toColorPreset,
} from '../../utils/helpers';
import { getIconClasses } from '../../utils/helpers';

/**
 * Generate a unique hotspot ID from existing hotspots.
 *
 * @param {Array} hotspots Existing hotspots array.
 * @return {string} New unique ID.
 */
function generateHotspotId( hotspots ) {
	const max = hotspots.reduce( ( acc, h ) => {
		const n = parseInt( h.id?.replace( 'hotspot-', '' ) || '0', 10 );
		return n > acc ? n : acc;
	}, 0 );
	return `hotspot-${ max + 1 }`;
}

/**
 * Clamp a value between min and max.
 *
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @return {number}
 */
function clamp( value, min, max ) {
	return Math.max( min, Math.min( max, value ) );
}

/**
 * Compute x/y percentage from a pointer event and a container rect.
 *
 * @param {number} clientX
 * @param {number} clientY
 * @param {DOMRect} rect
 * @return {{ x: number, y: number }}
 */
function computePercent( clientX, clientY, rect ) {
	return {
		x: clamp( ( ( clientX - rect.left ) / rect.width ) * 100, 0, 100 ),
		y: clamp( ( ( clientY - rect.top ) / rect.height ) * 100, 0, 100 ),
	};
}

/**
 * Product picker backed by WooCommerce post type via getEntityRecords.
 * Falls back to a numeric ID TextControl when WooCommerce is inactive.
 *
 * @param {Object}   props
 * @param {number}   props.productId  Current product ID (0 = none).
 * @param {Function} props.onChange   Callback with new product ID (number).
 */
function ProductPicker( { productId, onChange } ) {
	const [ search, setSearch ] = useState( '' );

	const { hasWooCommerce, products } = useSelect(
		( select ) => {
			const { getPostTypes, getEntityRecords } = select( coreStore );
			const postTypes = getPostTypes( { per_page: -1 } );
			const wc = postTypes?.some( ( pt ) => pt.slug === 'product' ) ?? false;
			return {
				hasWooCommerce: wc,
				products: wc
					? getEntityRecords( 'postType', 'product', {
							search,
							per_page: 10,
							status: 'publish',
					  } ) ?? []
					: [],
			};
		},
		[ search ]
	);

	// Must be called before any conditional return to obey React Rules of Hooks.
	const currentProduct = useSelect(
		( select ) => {
			if ( ! productId ) return null;
			return select( coreStore ).getEntityRecord( 'postType', 'product', productId );
		},
		[ productId ]
	);

	if ( ! hasWooCommerce ) {
		return (
			<TextControl
				label={ __( 'Product ID', 'brandy-blocks' ) }
				help={ __( 'WooCommerce inactive — enter product ID manually (0 = none).', 'brandy-blocks' ) }
				type="number"
				value={ productId || '' }
				onChange={ ( val ) => onChange( parseInt( val, 10 ) || 0 ) }
			/>
		);
	}

	const options = products.map( ( p ) => ( {
		label: decodeEntities( p.title?.rendered || `#${ p.id }` ),
		value: String( p.id ),
	} ) );

	// Ensure the currently-saved product appears in the options list on reload.
	if ( productId && currentProduct && ! options.find( ( o ) => o.value === String( productId ) ) ) {
		options.unshift( {
			label: decodeEntities( currentProduct.title?.rendered || `#${ productId }` ),
			value: String( productId ),
		} );
	}

	return (
		<ComboboxControl
			label={ __( 'Link to product', 'brandy-blocks' ) }
			help={ __( 'Search for a product. Leave empty to show text-only tooltip.', 'brandy-blocks' ) }
			value={ productId ? String( productId ) : '' }
			options={ options }
			onInputChange={ setSearch }
			onChange={ ( val ) => onChange( val ? parseInt( val, 10 ) : 0 ) }
			allowReset
		/>
	);
}

/**
 * Edit component for Image Hotspots block.
 *
 * @param {Object}   props
 * @param {Object}   props.attributes
 * @param {Function} props.setAttributes
 * @param {string}   props.clientId
 */
export default function Edit( { attributes, setAttributes, clientId } ) {
	const {
		image,
		hotspots,
		markerStyle,
		markerColor,
		markerIconName,
		markerIconType,
	} = attributes;

	const [ palette ] = useSettings( 'color.palette' );

	const [ selectedIndex, setSelectedIndex ] = useState( null );
	const [ isIconsModalOpen, setIsIconsModalOpen ] = useState( false );

	// Mirrors render.php: an icon marker with no glyph picked yet would be an
	// empty button, so the preview shows the dot until one is chosen.
	const markerIconClasses = getIconClasses( markerIconName, markerIconType );
	const resolvedMarkerStyle =
		markerStyle === 'icon' && ! markerIconClasses ? 'dot' : markerStyle;
	const dragRef = useRef( null );
	const stageRef = useRef( null );

	const { selectBlock } = useDispatch( blockEditorStore );

	const blockProps = useBlockProps( { className: 'brandy-image-hotspots' } );

	// ── Image helpers ──────────────────────────────────────────────────────────

	const onSelectImage = ( media ) => {
		setAttributes( {
			image: {
				id: media.id,
				url: media.url,
				alt: media.alt || '',
				width: media.width || 0,
				height: media.height || 0,
			},
		} );
	};

	// External images carry no attachment row, so the id resets to 0 and the
	// dimensions stay unknown — render.php echoes the raw URL either way.
	const onSelectImageUrl = ( url ) => {
		setAttributes( {
			image: { id: 0, url, alt: '', width: 0, height: 0 },
		} );
	};

	const onRemoveImage = () => {
		setAttributes( { image: { id: 0, url: '', alt: '', width: 0, height: 0 } } );
		setSelectedIndex( null );
	};

	// ── Hotspot helpers ────────────────────────────────────────────────────────

	const updateHotspot = useCallback(
		( index, patch ) => {
			const next = hotspots.map( ( h, i ) =>
				i === index ? { ...h, ...patch } : h
			);
			setAttributes( { hotspots: next } );
		},
		[ hotspots, setAttributes ]
	);

	const deleteHotspot = ( index ) => {
		const next = hotspots.filter( ( _, i ) => i !== index );
		setAttributes( { hotspots: next } );
		setSelectedIndex( null );
	};

	// ── Stage click — add hotspot ──────────────────────────────────────────────

	const onStageClick = ( e ) => {
		// Ignore if click originated on a marker (handled by marker's own handler).
		if ( e.target.closest( '.brandy-hotspot-marker' ) ) return;
		if ( ! stageRef.current ) return;
		const rect = stageRef.current.getBoundingClientRect();
		const { x, y } = computePercent( e.clientX, e.clientY, rect );
		const newHotspot = {
			id: generateHotspotId( hotspots ),
			x,
			y,
			title: '',
			description: '',
			buttonText: '',
			buttonUrl: '',
			productId: 0,
		};
		const next = [ ...hotspots, newHotspot ];
		setAttributes( { hotspots: next } );
		setSelectedIndex( next.length - 1 );
	};

	// ── Drag ───────────────────────────────────────────────────────────────────

	const onMarkerMouseDown = ( e, index ) => {
		e.preventDefault();
		// Programmatically select the block so Inspector Controls appear even
		// when the block wasn't previously focused. Cannot rely on event bubbling
		// because preventDefault() on mousedown suppresses the browser focus event
		// that Gutenberg normally uses for block selection.
		selectBlock( clientId );
		setSelectedIndex( index );
		// Record the offset between click point and marker center so the marker
		// doesn't jump on first mousemove.
		if ( stageRef.current ) {
			const rect = stageRef.current.getBoundingClientRect();
			const clickX = ( ( e.clientX - rect.left ) / rect.width ) * 100;
			const clickY = ( ( e.clientY - rect.top ) / rect.height ) * 100;
			const h = hotspots[ index ];
			dragRef.current = { index, offsetX: clickX - h.x, offsetY: clickY - h.y };
		}
	};

	const onStageMouseMove = ( e ) => {
		if ( ! dragRef.current || ! stageRef.current ) return;
		e.preventDefault();
		const rect = stageRef.current.getBoundingClientRect();
		const cursorX = ( ( e.clientX - rect.left ) / rect.width ) * 100;
		const cursorY = ( ( e.clientY - rect.top ) / rect.height ) * 100;
		const x = clamp( cursorX - dragRef.current.offsetX, 0, 100 );
		const y = clamp( cursorY - dragRef.current.offsetY, 0, 100 );
		updateHotspot( dragRef.current.index, { x, y } );
	};

	const onStageMouseUp = () => {
		dragRef.current = null;
	};

	// Touch drag.
	const onMarkerTouchStart = ( e, index ) => {
		e.stopPropagation();
		setSelectedIndex( index );
		if ( stageRef.current ) {
			const touch = e.touches[ 0 ];
			const rect = stageRef.current.getBoundingClientRect();
			const touchX = ( ( touch.clientX - rect.left ) / rect.width ) * 100;
			const touchY = ( ( touch.clientY - rect.top ) / rect.height ) * 100;
			const h = hotspots[ index ];
			dragRef.current = { index, offsetX: touchX - h.x, offsetY: touchY - h.y };
		}
	};

	const onStageTouchMove = ( e ) => {
		if ( ! dragRef.current || ! stageRef.current ) return;
		const touch = e.touches[ 0 ];
		const rect = stageRef.current.getBoundingClientRect();
		const touchX = ( ( touch.clientX - rect.left ) / rect.width ) * 100;
		const touchY = ( ( touch.clientY - rect.top ) / rect.height ) * 100;
		const x = clamp( touchX - dragRef.current.offsetX, 0, 100 );
		const y = clamp( touchY - dragRef.current.offsetY, 0, 100 );
		updateHotspot( dragRef.current.index, { x, y } );
	};

	const onStageTouchEnd = () => {
		dragRef.current = null;
	};

	// ── Render ─────────────────────────────────────────────────────────────────

	const selectedHotspot = selectedIndex !== null ? hotspots[ selectedIndex ] : null;

	return (
		<>
			{ /* Block toolbar — Replace image */ }
			{ image?.url && (
				<BlockControls>
					<MediaUploadCheck>
						{ /* Passing onSelectURL is what makes MediaReplaceFlow
						     show its own "Insert from URL" field; without it the
						     toolbar offers the library only. */ }
						<MediaReplaceFlow
							mediaId={ image.id }
							mediaURL={ image.url }
							allowedTypes={ [ 'image' ] }
							accept="image/*"
							onSelect={ onSelectImage }
							onSelectURL={ onSelectImageUrl }
							name={ __( 'Replace image', 'brandy-blocks' ) }
						/>
					</MediaUploadCheck>
				</BlockControls>
			) }

			{ /* Inspector — selected hotspot */ }
			<InspectorControls>
				{ selectedHotspot && (
					<PanelBody
						title={ __( 'Hotspot', 'brandy-blocks' ) }
						initialOpen={ true }
					>
						<TextControl
							label={ __( 'Title', 'brandy-blocks' ) }
							value={ selectedHotspot.title }
							onChange={ ( val ) =>
								updateHotspot( selectedIndex, { title: val } )
							}
						/>
						<TextareaControl
							label={ __( 'Description', 'brandy-blocks' ) }
							value={ selectedHotspot.description }
							onChange={ ( val ) =>
								updateHotspot( selectedIndex, { description: val } )
							}
						/>
						<TextControl
							label={ __( 'Button label', 'brandy-blocks' ) }
							value={ selectedHotspot.buttonText }
							onChange={ ( val ) =>
								updateHotspot( selectedIndex, { buttonText: val } )
							}
						/>
						<TextControl
							label={ __( 'Button URL', 'brandy-blocks' ) }
							type="url"
							value={ selectedHotspot.buttonUrl }
							onChange={ ( val ) =>
								updateHotspot( selectedIndex, { buttonUrl: val } )
							}
						/>
						<ProductPicker
							productId={ selectedHotspot.productId }
							onChange={ ( val ) =>
								updateHotspot( selectedIndex, { productId: val } )
							}
						/>
						<Button
							variant="link"
							isDestructive
							onClick={ () => deleteHotspot( selectedIndex ) }
							style={ { marginTop: '8px' } }
						>
							{ __( 'Delete hotspot', 'brandy-blocks' ) }
						</Button>
					</PanelBody>
				) }
				{ ! selectedHotspot && (
					<PanelBody
						title={ __( 'Hotspot', 'brandy-blocks' ) }
						initialOpen={ true }
					>
						<p className="brandy-hotspots-inspector-hint">
							{ __( 'Click the image to add a hotspot, or click an existing marker to edit it.', 'brandy-blocks' ) }
						</p>
					</PanelBody>
				) }

				{ /* Block-level settings */ }
				<PanelBody
					title={ __( 'Marker settings', 'brandy-blocks' ) }
					initialOpen={ true }
				>
					<SelectControl
						label={ __( 'Marker style', 'brandy-blocks' ) }
						value={ markerStyle }
						options={ [
							{ label: __( 'Dot (pulsing)', 'brandy-blocks' ), value: 'dot' },
							{ label: __( 'Number', 'brandy-blocks' ), value: 'number' },
							{ label: __( 'Icon', 'brandy-blocks' ), value: 'icon' },
						] }
						onChange={ ( val ) => setAttributes( { markerStyle: val } ) }
					/>

					{ markerStyle === 'icon' && (
						<div className="brandy-hotspot-icon-picker">
							{ markerIconClasses && (
								<i
									className={ markerIconClasses }
									aria-hidden="true"
								/>
							) }
							<Button
								variant="secondary"
								onClick={ () => setIsIconsModalOpen( true ) }
							>
								{ markerIconName
									? __( 'Change icon', 'brandy-blocks' )
									: __( 'Choose icon', 'brandy-blocks' ) }
							</Button>
							{ markerIconName && (
								<Button
									variant="tertiary"
									isDestructive
									onClick={ () =>
										setAttributes( { markerIconName: '' } )
									}
								>
									{ __( 'Remove', 'brandy-blocks' ) }
								</Button>
							) }
						</div>
					) }
				</PanelBody>
				<PanelColorSettings
					title={ __( 'Marker color', 'brandy-blocks' ) }
					initialOpen={ false }
					colorSettings={ [
						{
							label: __( 'Color', 'brandy-blocks' ),
							value: fromColorPreset( markerColor, palette ),
							onChange: ( val ) =>
								setAttributes( {
									markerColor: toColorPreset( val, palette ) || '',
								} ),
						},
					] }
				/>
			</InspectorControls>

			{ /* Block canvas */ }
			<div { ...blockProps }>
				{ ! image?.url ? (
					/* Deliberately not wrapped in MediaUploadCheck: inserting by
					   URL never touches the media library, so it needs no upload
					   rights — MediaPlaceholder makes the same split internally. */
					<MediaPlaceholder
						onSelect={ onSelectImage }
						onSelectURL={ onSelectImageUrl }
						accept="image/*"
						allowedTypes={ [ 'image' ] }
						icon="format-image"
						labels={ {
							title: __( 'Image Hotspots', 'brandy-blocks' ),
							instructions: __(
								'Select an image to add interactive hotspot markers. Pick one from your library, upload one, or insert one from a URL.',
								'brandy-blocks'
							),
						} }
					/>
				) : (
					/* Stage */
					<div
						className="brandy-image-hotspots__stage"
						ref={ stageRef }
						onClick={ onStageClick }
						onMouseMove={ onStageMouseMove }
						onMouseUp={ onStageMouseUp }
						onMouseLeave={ onStageMouseUp }
						onTouchMove={ onStageTouchMove }
						onTouchEnd={ onStageTouchEnd }
						role="button"
						tabIndex={ 0 }
						aria-label={ __( 'Click to add hotspot', 'brandy-blocks' ) }
					>
						<img
							src={ image.url }
							alt={ image.alt }
							className="brandy-image-hotspots__image"
							draggable={ false }
						/>

						{ hotspots.map( ( hotspot, index ) => (
							<button
								key={ hotspot.id }
								type="button"
								className={ `brandy-hotspot-marker brandy-hotspot-marker--${ resolvedMarkerStyle }${ index === selectedIndex ? ' is-selected' : '' }` }
								style={ {
									left: `${ hotspot.x }%`,
									top: `${ hotspot.y }%`,
									// Expanded as render.php does via get_ref_color().
									'--brandy-marker-color':
										resolvePresetValue( markerColor ) || undefined,
								} }
								onMouseDown={ ( e ) => onMarkerMouseDown( e, index ) }
								onTouchStart={ ( e ) => onMarkerTouchStart( e, index ) }
								onClick={ () => setSelectedIndex( index ) }
								aria-label={
									hotspot.title
										? hotspot.title
										: `${ __( 'Hotspot', 'brandy-blocks' ) } ${ index + 1 }`
								}
							>
								{ resolvedMarkerStyle === 'number' && (
									<span className="brandy-hotspot-marker__number">
										{ index + 1 }
									</span>
								) }
								{ resolvedMarkerStyle === 'icon' && (
									<i
										className={ markerIconClasses }
										aria-hidden="true"
									/>
								) }
								{ resolvedMarkerStyle === 'dot' && (
									<span className="brandy-hotspot-marker__dot" />
								) }
							</button>
						) ) }
					</div>
				) }
			</div>

			<IconsModal
				isIconsModalOpen={ isIconsModalOpen }
				setIsIconsModalOpen={ setIsIconsModalOpen }
				icon={ { name: markerIconName, type: markerIconType } }
				setAttributes={ ( newAttrs ) =>
					setAttributes( {
						markerIconName: newAttrs.name,
						markerIconType: newAttrs.type,
					} )
				}
			/>
		</>
	);
}
